import { Options, Point, TooltipFormatterContextObject } from 'highcharts';

import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { formatKpi, Kpi } from '@utils/Formatters';
import { buildDataForSeries, buildXAxisLabel, chartOption, TooltipSeriesPoint } from '../utilities/CommonOptions';

import * as styles from './ShareComparisonChart.module.scss';
import { BaseChartResp, ShareComparisonChartResp } from './Types';
import { FactRevenue } from '@utils/pageConfig/Types';
import { constructRevenueTitle } from '@utils/currencyUtils/CurrencyUtils';

const revenueShareIndex = 2; //The index value of revenue share, passed from ShareComparisonCard

export const buildSeriesName = (
  initialLegendName: string,
  isValue: boolean,
  revenueTitle: string,
  volumeTitle: string,
): string => {
  return `${initialLegendName} ${isValue ? revenueTitle : volumeTitle}`;
};

const tooltipGenerator = (isValue: boolean, points: TooltipFormatterContextObject[], currency: string) => {
  return `
    <div class="${styles.ShareComparisonChart__ToolTip__Content}">
      <p class="${styles.ShareComparisonChart__ToolTip__Heading}">
        <span>${points[0]?.x}</span>
      </p>
      ${buildTooltipLineItem(isValue, points, currency)}
    </div>
  `;
};

/* eslint-disable indent */
const buildTooltipLineItem = (
  isValue: boolean, //PerformanceType.Value=true, PerformanceType.Units=false
  points: TooltipFormatterContextObject[],
  currency: string,
): string => {
  return points
    .map((point: { y: number; series: TooltipSeriesPoint; point: Point }) => {
      return `
        <div class="${styles.ShareComparisonChart__ToolTip__Row}">
          <span class="${
            point.series.index === revenueShareIndex
              ? styles.ShareComparisonChart__ToolTip__Key
              : styles.ShareComparisonChart__ToolTip__Key__Dot
          }" style="background-color: ${point.series.color}">
          </span>
          <span class="${styles.ShareComparisonChart__ToolTip__Label}">
            ${point.series.name}
          </span>
          <span class="${styles.ShareComparisonChart__ToolTip__Value}">
            ${
              isValue
                ? buildRevenueTooltip(point.series.index, point.y, currency)
                : buildVolumeTooltip(point.series.index, point.y)
            }
          </span>
        </div>`;
    })
    .join('');
};

const buildRevenueTooltip = (seriesIndex: number, pointY: number, currency: string): string => {
  if (seriesIndex === revenueShareIndex) {
    return formatKpi(Kpi.revenueShare, pointY, {
      isDecimalPercent: false,
    });
  } else {
    return formatKpi(Kpi.retailerRevenue, pointY, {
      isDecimalPercent: true,
      currencySymbol: currency,
    });
  }
};
const buildVolumeTooltip = (seriesIndex: number, pointY: number): string => {
  if (seriesIndex === revenueShareIndex) {
    return formatKpi(Kpi.revenueShare, pointY, {
      isDecimalPercent: false,
    });
  } else {
    return formatKpi(Kpi.salesVolume, pointY);
  }
};
const isPositiveOrZeroNumber = (data: BaseChartResp[]): boolean => {
  let minValue = 0;

  data.forEach((item) => {
    if (item.value < minValue) {
      minValue = item.value;
    }
  });

  return Math.sign(minValue) === 1 || Math.sign(minValue) === 0;
};

const setMin = (data: BaseChartResp[]) => {
  return isPositiveOrZeroNumber(data) ? 0 : null;
};

export const getChartOptions = (
  data: ShareComparisonChartResp,
  isValue: boolean,
  selectedPeriodicity: Periodicity,
  factRevenue: FactRevenue,
  volumeTitle: string,
): Options => {
  if (!data) return {};
  const revenueTitle = constructRevenueTitle(factRevenue);
  return {
    title: chartOption.title,
    credits: chartOption.credits,
    plotOptions: chartOption.plotOptions,
    tooltip: {
      backgroundColor: '#FFFFFF',
      shared: true,
      borderRadius: 3,
      borderWidth: 0,
      padding: 5,
      useHTML: true,
      formatter: function (): string {
        const points: TooltipFormatterContextObject[] | undefined = this.points;
        if (!points) {
          return '';
        }
        return tooltipGenerator(isValue, points, factRevenue.currencySymbol);
      },
    },
    xAxis: {
      categories: isValue
        ? buildXAxisLabel(data.value.marketValue, selectedPeriodicity)
        : buildXAxisLabel(data.volume.marketVolume, selectedPeriodicity),
      crosshair: {
        dashStyle: 'Dash',
        width: 1,
      },
    },
    yAxis: [
      {
        alignTicks: false,
        tickAmount: 5,
        title: {
          text: isValue ? revenueTitle : volumeTitle,
        },
        labels: {
          formatter() {
            if (isValue) {
              return formatKpi(Kpi.marketRevenue, +this.value, {
                currencySymbol: factRevenue.currencySymbol,
              });
            }
            return formatKpi(Kpi.marketVolume, +this.value);
          },
        },
      },
      {
        alignTicks: false,
        tickAmount: 5,
        labels: {
          formatter() {
            return formatKpi(Kpi.retailerShareVolume, +this.value);
          },
        },
        title: {
          text: 'Share',
        },
        opposite: true,
        min: isValue ? setMin(data.value.retailerValueShare) : setMin(data.volume.retailerVolumeShare),
      },
    ],
    series: [
      {
        yAxis: 0,
        groupPadding: 0.5,
        pointWidth: 40,
        type: 'column',
        name: buildSeriesName('Market', isValue, revenueTitle, volumeTitle),
        color: '#D6D9DC',
        data: isValue ? buildDataForSeries(data.value.marketValue) : buildDataForSeries(data.volume.marketVolume),
      },
      {
        yAxis: 0,
        groupPadding: 0.5,
        pointWidth: 30,
        type: 'column',
        name: buildSeriesName('Retailer', isValue, revenueTitle, volumeTitle),
        color: '#879BCC',
        data: isValue ? buildDataForSeries(data.value.retailerValue) : buildDataForSeries(data.volume.retailerVolume),
      },
      {
        yAxis: 1,
        type: 'line',
        name: `${buildSeriesName('Retailer', isValue, revenueTitle, volumeTitle)} Share`,
        color: '#00008B',
        data: isValue
          ? buildDataForSeries(data.value.retailerValueShare)
          : buildDataForSeries(data.volume.retailerVolumeShare),
      },
    ],
  };
};
